import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { forexApi, type RiskConfig } from "@/lib/forex-api";

interface KillSwitchConfig {
  id: string;
  masterAccount: string;
  broker: string;
  accountLabel: string;
  currentState: "on" | "off";
  updatedAt: string;
  affectedSubAccounts: number | null;
}

function toKillSwitch(item: RiskConfig): KillSwitchConfig {
  return {
    id: item.risk_config_id,
    masterAccount: item.scope,
    broker: "未提供",
    accountLabel: `${item.scope} 风控配置`,
    currentState: item.kill_switch,
    updatedAt: Number.isFinite(item.updated_at) ? new Date(item.updated_at * 1000).toLocaleString("sv-SE").slice(0, 16) : "未提供",
    affectedSubAccounts: null,
  };
}

export default function KillSwitchTab() {
  const navigate = useNavigate();
  const [killSwitches, setKillSwitches] = useState<KillSwitchConfig[]>([]);
  const [confirmTarget, setConfirmTarget] = useState<string | null>(null);
  const [confirmReason, setConfirmReason] = useState("");
  const [actionStates, setActionStates] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [remoteReady, setRemoteReady] = useState(false);
  const [toast, setToast] = useState<{ type: "success" | "error"; msg: string } | null>(null);

  const showToast = (type: "success" | "error", msg: string) => { setToast({ type, msg }); setTimeout(() => setToast(null), 3000); };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    forexApi.listRiskConfigs()
      .then((result) => {
        if (cancelled) return;
        setKillSwitches(result.items.map(toKillSwitch));
        setRemoteReady(true);
      })
      .catch(() => {
        if (!cancelled) {
          setRemoteReady(false);
          showToast("error", "后端风控配置接口不可用，请稍后重试");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const handleToggle = async (id: string, target: "on" | "off") => {
    if (!confirmReason.trim()) return;
    setActionStates((p) => ({ ...p, [id]: "toggling" }));
    try {
      const updated = await forexApi.patchRiskConfig(id, {
        kill_switch: target,
        reason: confirmReason,
      });
      setKillSwitches((prev) => prev.map((item) => (item.id === id ? toKillSwitch(updated) : item)));
      setActionStates((p) => ({ ...p, [id]: "toggled" }));
      showToast("success", `后台配置记录已保存（${target === "on" ? "开启" : "关闭"}）；执行生效状态请单独核对：${confirmReason}`);
      setConfirmTarget(null);
      setConfirmReason("");
    } catch {
      showToast("error", "Kill Switch 操作失败，请确认后端服务和登录态");
    } finally {
      setTimeout(() => setActionStates((p) => { const n = { ...p }; delete n[id]; return n; }), 2000);
    }
  };

  const anyOn = killSwitches.some((k) => k.currentState === "on");

  return (
    <div className="space-y-4">
      <p role="status" className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm">此页展示后台配置记录；当前账号是否被阻断，请在智能体的研究与账号执行中核对有效审核配置、授权与逐账号风险结果。保存记录不代表执行风控已生效。</p>
      <div>
        <h2 className="text-lg font-semibold text-text-primary">Kill Switch</h2>
        <p className="text-xs text-text-muted mt-0.5">修改后台 Kill Switch 配置记录需要输入原因；实际执行阻断请单独核对。</p>
        <p className={`text-[10px] mt-1 ${remoteReady ? "text-emerald-500" : "text-amber-500"}`}>
          {loading ? "正在同步风控配置..." : remoteReady ? "已接入 /quantum/business/forex/risk-configs" : "风控配置加载失败，状态未知"}
        </p>
      </div>

      <div className={`rounded-lg border p-4 ${!remoteReady || killSwitches.length === 0 ? "bg-amber-500/5 border-amber-500/20" : anyOn ? "bg-red-500/5 border-red-500/20" : "bg-emerald-500/5 border-emerald-500/20"}`}>
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${!remoteReady || killSwitches.length === 0 ? "bg-amber-500/10 text-amber-500" : anyOn ? "bg-red-500/10 text-red-500" : "bg-emerald-500/10 text-emerald-500"}`}>
            <i className={`text-xl ${!remoteReady || killSwitches.length === 0 ? "ri-information-line" : anyOn ? "ri-shut-down-fill" : "ri-checkbox-circle-fill"}`} />
          </div>
          <div>
            <p className={`text-sm font-semibold ${!remoteReady || killSwitches.length === 0 ? "text-amber-600" : anyOn ? "text-red-500" : "text-emerald-500"}`}>
              Kill Switch 配置记录：{loading ? "加载中" : !remoteReady ? "加载失败，状态未知" : killSwitches.length === 0 ? "暂无记录，状态未知" : anyOn ? `${killSwitches.filter((k) => k.currentState === "on").length} 条记录为开启` : "已取得记录均为关闭"}
            </p>
            <p className="text-xs text-text-muted mt-0.5">
              {anyOn && remoteReady ? "存在开启的后台配置记录；实际影响范围尚未核验" : "此处只反映已取得的配置记录，不代表运行态或执行准入结果"}
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-surface-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1100px]">
            <thead>
              <tr className="border-b border-surface-border bg-surface/50">
                <th className="text-left px-4 py-2.5 text-text-secondary font-medium text-[11px]">配置范围</th>
                <th className="text-left px-4 py-2.5 text-text-secondary font-medium text-[11px]">经纪商信息</th>
                <th className="text-center px-4 py-2.5 text-text-secondary font-medium text-[11px]">当前状态</th>
                <th className="text-left px-4 py-2.5 text-text-secondary font-medium text-[11px]">操作人</th>
                <th className="text-left px-4 py-2.5 text-text-secondary font-medium text-[11px]">记录更新时间</th>
                <th className="text-left px-4 py-2.5 text-text-secondary font-medium text-[11px]">原因</th>
                <th className="text-center px-4 py-2.5 text-text-secondary font-medium text-[11px]">影响子账号</th>
                <th className="text-center px-4 py-2.5 text-text-secondary font-medium text-[11px]">操作</th>
              </tr>
            </thead>
            <tbody>
              {killSwitches.map((k) => {
                const isConfirming = confirmTarget === k.id;
                const act = actionStates[k.id];
                const targetAction = k.currentState === "on" ? "off" : "on";
                return (
                  <tr key={k.id} className={`border-b border-surface-border hover:bg-surface/30 transition-colors ${k.currentState === "on" ? "bg-red-500/[0.02]" : ""}`}>
                    <td className="px-4 py-2.5">
                      <p className="text-text-primary text-xs font-medium">{k.accountLabel}</p>
                      <p className="text-text-muted text-[10px] font-mono">{k.masterAccount}</p>
                    </td>
                    <td className="px-4 py-2.5 text-text-muted text-xs">{k.broker}</td>
                    <td className="px-4 py-2.5 text-center">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold ${k.currentState === "on" ? "bg-red-500 text-white" : "bg-emerald-500/10 text-emerald-500"}`}>
                        <i className={`text-[10px] ${k.currentState === "on" ? "ri-shut-down-fill" : "ri-checkbox-circle-fill"}`} />
                        {k.currentState === "on" ? "记录为开启" : "记录为关闭"}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-text-secondary text-xs">未提供</td>
                    <td className="px-4 py-2.5 text-text-muted text-[11px] whitespace-nowrap">{k.updatedAt}</td>
                    <td className="px-4 py-2.5 text-text-secondary text-[11px] max-w-[200px]">未提供</td>
                    <td className="px-4 py-2.5 text-center"><span className={`font-mono text-xs ${(k.affectedSubAccounts ?? 0) > 0 ? "text-red-500 font-medium" : "text-text-muted"}`}>{k.affectedSubAccounts ?? "未核验"}</span></td>
                    <td className="px-4 py-2.5 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {isConfirming ? (
                          <div className="flex items-center justify-center gap-1">
                            <input type="text" value={confirmReason} onChange={(e) => setConfirmReason(e.target.value)} placeholder="输入原因..." className="w-[140px] h-7 px-2 text-[11px] rounded border border-red-500/30 bg-white focus:border-red-500 focus:outline-none" autoFocus />
                            <button onClick={() => handleToggle(k.id, targetAction)} disabled={!confirmReason.trim() || act === "toggling"} className="px-2 py-1 rounded text-[10px] font-bold bg-red-500 text-white hover:bg-red-600 disabled:opacity-50 whitespace-nowrap">
                              {act === "toggling" ? <i className="ri-loader-4-line animate-spin" /> : "确认"}
                            </button>
                            <button onClick={() => { setConfirmTarget(null); setConfirmReason(""); }} className="px-2 py-1 rounded text-[10px] text-text-muted hover:text-red-500">取消</button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setConfirmTarget(k.id)}
                            disabled={!!act}
                            className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-bold transition-all whitespace-nowrap ${
                              act === "toggled"
                                ? (targetAction === "on" ? "bg-red-500 text-white" : "bg-emerald-500 text-white")
                                : k.currentState === "on"
                                ? "bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/20"
                                : "bg-red-500/10 text-red-500 hover:bg-red-500/20"
                            }`}
                          >
                            {act === "toggled" ? (
                              <><i className="ri-check-line text-xs" />{targetAction === "on" ? "记录已开启" : "记录已关闭"}</>
                            ) : (
                              <><i className={`text-xs ${k.currentState === "on" ? "ri-play-circle-line" : "ri-shut-down-line"}`} />{k.currentState === "on" ? "关闭记录" : "开启记录"}</>
                            )}
                          </button>
                        )}
                        <Link to={`/forex/kill-switch/detail?id=${k.id}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-surface text-text-secondary hover:bg-brand/10 hover:text-brand whitespace-nowrap cursor-pointer">
                          <i className="ri-eye-line text-xs" />详情
                        </Link>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {!loading && killSwitches.length === 0 && (
          <div className="px-4 py-10 text-center text-sm text-text-muted">{remoteReady ? "暂无 Kill Switch 配置记录，无法判断运行状态" : "配置记录加载失败，状态未知"}</div>
        )}
      </div>
      {toast && (
        <div className="fixed bottom-6 right-6 z-50 animate-[slideUp_0.3s_ease-out]">
          <div className={`flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${toast.type === "success" ? "bg-emerald-500 text-white" : "bg-red-500 text-white"}`}>
            <i className={`text-base ${toast.type === "success" ? "ri-checkbox-circle-fill" : "ri-error-warning-fill"}`} />{toast.msg}
          </div>
        </div>
      )}
    </div>
  );
}
